import React, { MouseEvent, useRef } from 'react';
import isEqual from 'react-fast-compare';

import { VariantProps, cn, tv } from '@eluve/components';

import { useSegmentAudioContext } from './SegmentAudioContextStore';

const transcriptConfidenceStyles = tv({
  base: '',
  variants: {
    confidence: {
      10: 'opacity-100',
      9: 'opacity-95',
      8: 'opacity-90',
      7: 'opacity-80',
      6: 'opacity-70',
      5: 'opacity-60',
      4: 'opacity-50',
      3: 'opacity-40',
      2: 'opacity-30',
      1: 'opacity-20',
      0: 'opacity-15',
    },
  },
});

type TranscriptConfidenceStyleVariantProps = VariantProps<
  typeof transcriptConfidenceStyles
>;

const TranscriptWord: React.FC<{
  className?: string;
  showConfidenceStyles: boolean;
  u: {
    id: string;
    start: number | null;
    end: number | null;
  };
  w: {
    confidence: number | null;
    punctuated_word?: string;
    word: string;
    start: number | null;
    end: number | null;
    fragmentId: string;
    speaker: string | null;
  };
}> = ({ u, w, showConfidenceStyles, className }) => {
  const { audioPlayerHandle } = useSegmentAudioContext();
  const tooltipLabel = `Word: "${w.punctuated_word ?? w.word}", Confidence: ${w.confidence}`;
  const shouldFireClick = useRef<boolean>(true);
  const handleMouseDown = (e: MouseEvent) => {
    const activeElement = document.activeElement;
    const contentEditableDiv = (e.target as HTMLElement).closest(
      '[contenteditable="true"]',
    );
    if (contentEditableDiv && contentEditableDiv === activeElement) {
      e.stopPropagation();
      shouldFireClick.current = false;
      return;
    }
    shouldFireClick.current = true;
  };

  const handleWordClick = () => {
    if (w.start === null || !shouldFireClick.current) return;
    audioPlayerHandle?.current?.seekTo(w.start);
    if (u.start && u.end) {
      audioPlayerHandle.current?.setSectionStartEnd(u.start, u.end);
    }
    audioPlayerHandle?.current?.play();
  };

  return (
    <span
      onClick={handleWordClick}
      onMouseDown={handleMouseDown}
      data-tooltip-label={tooltipLabel}
      data-karaoke-word-id={w.fragmentId}
      className={cn(
        className,
        'z-20 inline-flex cursor-pointer text-md font-medium text-text-primary group-focus/line:cursor-auto group-focus/line:no-underline hover:underline data-karaoke-word-active:bg-bg-orange-strong',
        showConfidenceStyles &&
          w.confidence &&
          transcriptConfidenceStyles({
            confidence: Math.floor(
              w.confidence * 10,
            ) as TranscriptConfidenceStyleVariantProps['confidence'],
          }),
      )}
    >
      {w.punctuated_word ?? w.word}
    </span>
  );
};

export const MemoizedTranscriptWord = React.memo(TranscriptWord, isEqual);
